<template>
	<div class="form-group flex flex-col">
		<label class="w-full" :style="`font-weight: ${font}`" v-if="label">
			{{ label }}
			<span v-if="required && !disabled" class="text-red-600" :class="font"> *</span>
		</label>
		<Multiselect
			ref="Multiselect"
			:options="options"
			:searchable="isSearchable"
			:allow-empty="allowEmpty"
			:placeholder="placeholder"
			select-label=""
			class="w-full"
			:selected-label="selectedLabel"
			:deselect-label="$t('remove')"
			v-model="model"
			:disabled="disabled"
			:label="optionsLabel"
			:multiple="multiple"
			:close-on-select="closeOnSelectResolved"
			:track-by="trackBy || optionsLabel"
			:max="max"
			:limit="itemLimit"
			:limit-text="limitText"
			:options-limit="optionsLimit"
			@input="$emit('changed')"
			@remove="$emit('removeSelect', $event)"
		>
			<template #noOptions>
				{{ $t('no_elements') }}
			</template>
			<template #noResult>
				{{ $t('no_result') }}
			</template>
			<template #maxElements>
				{{ $t('max_amount_selected') }}
			</template>
			<template slot="option" slot-scope="props">
				<div class="option__desc" :class="{ 'text-gray-500': props.option.grayed }">
					<font-awesome-icon :icon="['fas', 'plus']" v-if="props.option.highlight" class="mr-3"></font-awesome-icon>
					<span class="option__title" :class="{ 'font-bold': props.option.highlight }"
						>{{ props.option.name ? props.option.name : props.option.display }}
						{{ props.option.premiumReferral ? '- Premium Partner' : '' }}
						{{ props.option.highlight ? props.option.highlight_suffix || $t('add').toLowerCase() : '' }}</span
					>
				</div>
			</template>
			<!-- <template slot="limit" slot-scope=" { count } ">
        +{{count}}
      </template>-->
			<!--	<template v-slot:tag="{ option }">
				<span class="multiselect__single mb-2">{{ option[optionsLabel] }}</span>
			</template>-->
		</Multiselect>

		<div class="input-errors" v-if="!isValid && !disabled">
			{{ errorMessage || getDefaultErrorsMessage }}
		</div>
	</div>
</template>

<script>
import Multiselect from 'vue-multiselect';

export default {
	name: 'BaseSelect',
	components: {
		Multiselect,
	},
	props: {
		itemDesktopLimit: { default: 3 },
		itemMobileLimit: { default: 2 },
		optionsLimit: {},
		placeholder: {
			default: function() {
				return this.$t('please_pick');
			},
		},
		options: {
			required: true,
		},
		value: {},
		required: {},
		label: {},
		max: {
			default: null,
		},
		errorMessage: {},
		validation: {},
		disabled: { default: false },
		optionsLabel: {
			default: 'display',
		},
		allowEmpty: {
			default: false,
		},
		multiple: {
			default: false,
		},
		disableSearch: { default: true },
		trackBy: { type: String },
		selectedLabel: {
			default: function() {
				return this.$t('selected');
			},
		},
		font: { default: 'bold' },
		filterOnly: { default: false },
		closeOnSelectOverride: { type: Boolean, default: null },
	},
	methods: {
		limitText(count) {
			return `+${count}`;
		},
		validate() {
			let isValid = true;
			if (!this.disabled) {
				if (this.required && isValid) {
					isValid = isValid && this.value && this.value.length !== 0;
				}
			}

			this.isValid = isValid;
			return this.isValid;
		},
		getMultiselectLabel(values) {
			return values
				.map(value => {
					return value[this.optionsLabel];
				})
				.join(', ');
		},
		close() {
			this.$refs.Multiselect.deactivate();
		},
	},
	data() {
		return {
			isValid: true,
		};
	},
	watch: {
		value: function() {
			this.validate();
		},
	},
	computed: {
		closeOnSelectResolved() {
			return this.closeOnSelectOverride ?? !this.multiple;
		},
		itemLimit() {
			if (this.$store.getters.getIsMobileView) return this.itemMobileLimit;
			return this.itemDesktopLimit;
		},
		isSearchable() {
			if (this.disableSearch) return false;
			const isMinimumCountReached = this.options.length > 3;
			const isOptionsCountSameAsModelCount = this.model?.length === this.options.length;
			return (isMinimumCountReached && !this.model) || (isMinimumCountReached && !isOptionsCountSameAsModelCount);
		},
		getDefaultErrorsMessage: function() {
			if (this.label && this.required) {
				return this.label + ' ' + this.$t('is_required');
			}

			return this.$t('field_not_validated');
		},
		model: {
			get() {
				return this.value;
			},
			set(v) {
				if (!this.filterOnly) {
					this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
				}
				this.$emit('input', v);
			},
		},
	},
};
</script>

<style>
.form-group input[type='input'] {
	height: 43px;
}

.multiselect__single {
	text-overflow: ellipsis;
	overflow: hidden;
}

.option__title {
	white-space: break-spaces;
	word-break: break-word;
}

fieldset[disabled] .multiselect {
	pointer-events: none;
}

.multiselect__spinner {
	position: absolute;
	right: 1px;
	top: 1px;
	width: 48px;
	height: 35px;
	background: #fff;
	display: block;
}

.multiselect__spinner:after,
.multiselect__spinner:before {
	position: absolute;
	content: '';
	top: 50%;
	left: 50%;
	margin: -8px 0 0 -8px;
	width: 16px;
	height: 16px;
	border-radius: 100%;
	border: 2px solid transparent;
	border-top-color: #0068b4;
	box-shadow: 0 0 0 1px transparent;
}

.multiselect__spinner:before {
	animation: spinning 2.4s cubic-bezier(0.41, 0.26, 0.2, 0.62);
	animation-iteration-count: infinite;
}

.multiselect__spinner:after {
	animation: spinning 2.4s cubic-bezier(0.51, 0.09, 0.21, 0.8);
	animation-iteration-count: infinite;
}

.multiselect__loading-enter-active,
.multiselect__loading-leave-active {
	transition: opacity 0.4s ease-in-out;
	opacity: 1;
}

.multiselect__loading-enter,
.multiselect__loading-leave-active {
	opacity: 0;
}

.multiselect,
.multiselect__input,
.multiselect__single {
	font-family: inherit;
	font-size: 16px;
	-ms-touch-action: manipulation;
	touch-action: manipulation;
}

.multiselect {
	box-sizing: content-box;
	display: block;
	position: relative;
	width: 100%;
	min-height: 40px;
	text-align: left;
	color: #35495e;
}

.multiselect * {
	box-sizing: border-box;
}

.multiselect:focus {
	outline: none;
}

.multiselect--disabled {
	background: #e9ecef;
	pointer-events: none;
	border-radius: 5px;
}

.multiselect--active {
	z-index: 49;
}

.multiselect--active:not(.multiselect--above) .multiselect__current,
.multiselect--active:not(.multiselect--above) .multiselect__input,
.multiselect--active:not(.multiselect--above) .multiselect__tags {
	border-bottom-left-radius: 0;
	border-bottom-right-radius: 0;
}

.multiselect--active .multiselect__select {
	transform: rotate(180deg);
}

.multiselect--above.multiselect--active .multiselect__current,
.multiselect--above.multiselect--active .multiselect__input,
.multiselect--above.multiselect--active .multiselect__tags {
	border-top-left-radius: 0;
	border-top-right-radius: 0;
}

.multiselect__input,
.multiselect__single {
	position: relative;
	display: inline-block;
	min-height: 20px;
	line-height: 20px;
	border: none;
	border-radius: 5px;
	background: #fff;
	padding: 0 0 0 5px;
	width: 100%;
	transition: border 0.1s ease;
	box-sizing: border-box;
	margin-bottom: 8px;
	vertical-align: top;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.multiselect__input:-ms-input-placeholder {
	color: #35495e;
}

.multiselect__input::placeholder {
	color: #35495e;
}

.multiselect__tag ~ .multiselect__input,
.multiselect__tag ~ .multiselect__single {
	width: auto;
}

.multiselect__input:hover,
.multiselect__single:hover {
	border-color: #cfcfcf;
}

.multiselect__input:focus,
.multiselect__single:focus {
	border-color: #a8a8a8;
	outline: none;
}

.multiselect__single {
	padding-left: 5px;
	margin-bottom: 8px;
}

.multiselect__tags-wrap {
	display: inline;
}

.multiselect__tags {
	min-height: 40px;
	display: block;
	padding: 8px 40px 0 8px;
	border-radius: 5px;
	border: 1px solid #e8e8e8;
	background: #fff;
	font-size: 14px;
}

.multiselect__tag {
	position: relative;
	display: inline-block;
	padding: 4px 26px 4px 10px;
	border-radius: 5px;
	margin-right: 10px;
	color: #fff;
	line-height: 1;
	background: #0068b4;
	margin-bottom: 5px;
	white-space: nowrap;
	overflow: hidden;
	max-width: 100%;
	text-overflow: ellipsis;
}

.multiselect__tag-icon {
	cursor: pointer;
	margin-left: 7px;
	position: absolute;
	right: 0;
	top: 0;
	bottom: 0;
	font-weight: 700;
	font-style: normal;
	width: 22px;
	text-align: center;
	line-height: 22px;
	transition: all 0.2s ease;
	border-radius: 5px;
}

.multiselect__tag-icon:after {
	content: '\D7';
	color: #0068b4;
	font-size: 14px;
}

.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
	background: #0068b4;
}

.multiselect__tag-icon:after,
.multiselect__tag-icon:after {
	color: #fff;
}

.multiselect__current {
	min-height: 40px;
	overflow: hidden;
	padding: 8px 30px 0 12px;
	white-space: nowrap;
	border-radius: 5px;
	border: 1px solid #e8e8e8;
}

.multiselect__current,
.multiselect__select {
	line-height: 16px;
	box-sizing: border-box;
	display: block;
	margin: 0;
	text-decoration: none;
	cursor: pointer;
}

.multiselect__select {
	position: absolute;
	width: 40px;
	height: 38px;
	right: 1px;
	top: 1px;
	padding: 4px 8px;
	text-align: center;
	transition: transform 0.2s ease;
}

.multiselect__select:before {
	position: relative;
	right: 0;
	top: 65%;
	color: #999;
	margin-top: 4px;
	border-color: #999 transparent transparent;
	border-style: solid;
	border-width: 5px 5px 0;
	content: '';
}

.multiselect__placeholder {
	color: #adadad;
	display: inline-block;
	margin-bottom: 10px;
	/*padding-top: 2px;*/
}

.multiselect--active .multiselect__placeholder {
	display: none;
}

.multiselect__content-wrapper {
	position: absolute;
	display: block;
	background: #fff;
	width: 100%;
	max-height: 200px;
	overflow: auto;
	border: 1px solid #e8e8e8;
	border-top: none;
	border-bottom-left-radius: 5px;
	border-bottom-right-radius: 5px;
	z-index: 49;
	-webkit-overflow-scrolling: touch;
	/*top: 38px;*/
}

.multiselect__content {
	list-style: none;
	display: inline-block;
	padding: 0;
	margin: 0;
	min-width: 100%;
	vertical-align: top;
}

.multiselect--above .multiselect__content-wrapper {
	bottom: 100%;
	border-bottom-left-radius: 0;
	border-bottom-right-radius: 0;
	border-top-left-radius: 5px;
	border-top-right-radius: 5px;
	border-bottom: none;
	border-top: 1px solid #e8e8e8;
}

.multiselect__content::webkit-scrollbar {
	display: none;
}

.multiselect__element {
	display: block;
}

.multiselect__option {
	display: block;
	padding: 12px;
	min-height: 40px;
	line-height: 16px;
	text-decoration: none;
	text-transform: none;
	vertical-align: middle;
	position: relative;
	cursor: pointer;
	white-space: nowrap;
}

.multiselect__option:after {
	top: 0;
	right: 0;
	position: absolute;
	line-height: 40px;
	padding-right: 12px;
	padding-left: 20px;
	font-size: 13px;
}

.multiselect__option--highlight {
	background: #0068b4;
	outline: none;
	color: #fff !important;
}

.multiselect__option--highlight:after {
	content: attr(data-select);
	background: #0068b4;
	color: #fff;
}

.multiselect__option--selected {
	background: #f3f3f3;
	color: #35495e;
	font-weight: 700;
}

.multiselect__option--selected:after {
	content: attr(data-selected);
	color: silver;
}

.multiselect--disabled .multiselect__current,
.multiselect--disabled .multiselect__single,
.multiselect--disabled .multiselect__select {
	background: #e9ecef;
}

.multiselect__option--disabled {
	background: #e9ecef !important;
	color: #a6a6a6 !important;
	cursor: text;
	pointer-events: none;
}

.multiselect__option--group {
	background: #e9ecef;
	color: #35495e;
}

.multiselect__option--group.multiselect__option--highlight {
	background: #35495e;
	color: #fff;
}

.multiselect__option--group.multiselect__option--highlight:after {
	background: #35495e;
}

.multiselect__option--disabled.multiselect__option--highlight {
	background: #dedede;
}

.multiselect__option--group-selected.multiselect__option--highlight {
	background: #ff6a6a;
	color: #fff;
}

.multiselect__option--group-selected.multiselect__option--highlight:after {
	background: #ff6a6a;
	content: attr(data-deselect);
	color: #fff;
}

.multiselect-enter-active,
.multiselect-leave-active {
	transition: all 0.15s ease;
}

.multiselect-enter,
.multiselect-leave-active {
	opacity: 0;
}

.multiselect__strong {
	margin-bottom: 8px;
	line-height: 20px;
	display: inline-block;
	vertical-align: top;
}

[dir='rtl'] .multiselect {
	text-align: right;
}

[dir='rtl'] .multiselect__select {
	right: auto;
	left: 1px;
}

[dir='rtl'] .multiselect__tags {
	padding: 8px 8px 0 40px;
}

[dir='rtl'] .multiselect__content {
	text-align: right;
}

[dir='rtl'] .multiselect__option:after {
	right: auto;
	left: 0;
}

[dir='rtl'] .multiselect__clear {
	right: auto;
	left: 12px;
}

[dir='rtl'] .multiselect__spinner {
	right: auto;
	left: 1px;
}

@keyframes spinning {
	0% {
		transform: rotate(0);
	}
	to {
		transform: rotate(2turn);
	}
}
</style>
<style lang="sass">
input:disabled
  background: #e9ecef

.multiselect__placeholder
  margin: 0

.multiselect
  min-height: 0

.multiselect__select
  height: calc(100% - 2px)

.multiselect__tags
@apply py-3
  line-height: 1
  min-height: 0
  font-size: 1rem

.multiselect__single
  padding: 0
  margin: 0
  min-height: 0
  line-height: 1

.multiselect__input
  border: none
  padding: 0 !important
  margin: 0 !important
  height: 20px !important

.multiselect__option .multiselect__option--highlight
  font-weight: 400
</style>
